/* ─── Invoke Activity bar labels (MS-702 AC12, AC14) ───

   The chart aggregates on the endpoint id, but the bar is labelled
   "Category > Description": an operation's group and its human name are what an
   admin recognises, whereas the endpoint id is an API identifier they rarely know
   by sight. The id stays the value being counted and remains searchable, so the
   term search matches either.

   Both parts come from the connection catalogue
   (`GET /api/v1/mambu-connections/core` → `operations[]`) and are data, not
   chrome — never translated (spec §4.7-22). */

export interface CatalogueOperation {
  key: string
  name: string
  category: string
}

export type OperationCatalogue = ReadonlyMap<string, CatalogueOperation>

export function buildOperationCatalogue(
  operations: readonly CatalogueOperation[],
): OperationCatalogue {
  return new Map(operations.map(op => [op.key, op]))
}

/**
 * `Category > Description`, or the raw id when the catalogue has no entry.
 *
 * Falling back rather than hiding is deliberate: the MCP server can expose an
 * operation the catalogue has not caught up with, and that is a data gap to
 * notice, not a bar to drop from a chart that claims to show every endpoint.
 */
export function operationLabel(operationKey: string, catalogue: OperationCatalogue): string {
  const entry = catalogue.get(operationKey)
  return entry ? `${entry.category} > ${entry.name}` : operationKey
}

/** Case-insensitive substring match against the label the reader sees *and* the
 *  underlying id. An empty or whitespace-only term means "no filter". */
export function matchesOperationQuery(
  operationKey: string,
  catalogue: OperationCatalogue,
  query: string,
): boolean {
  const term = query.trim().toLowerCase()
  if (!term) return true
  return (
    operationKey.toLowerCase().includes(term) ||
    operationLabel(operationKey, catalogue).toLowerCase().includes(term)
  )
}
